import React from "react";
import { toast } from "react-toastify";
import {
  Award, Tag, Printer, FileDown, Wrench,
  ClipboardCheck, Zap, LoaderPinwheel,
} from "lucide-react";

/* ── Helpers ─────────────────────────────────────────────────── */

const InfoField = ({ label, value }) => (
  <div className="space-y-0.5">
    <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">{label}</p>
    <p className="text-sm font-medium text-gray-800">{value || "—"}</p>
  </div>
);

const SectionCard = ({ icon: Icon, iconColor = "text-blue-500", title, children }) => (
  <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-5 space-y-4">
    <div className="flex items-center gap-2">
      <Icon className={`h-4 w-4 shrink-0 ${iconColor}`} />
      <h4 className="text-sm font-semibold text-gray-700">{title}</h4>
    </div>
    {children}
  </div>
);

/* ── Component ───────────────────────────────────────────────── */

const FichaTecnica = ({ equipo, mantenimiento, publicView = false }) => {
  const [loading, setLoading] = React.useState(null);

  const garantiaActiva =
    equipo?.garantia && equipo.garantia.toLowerCase() !== "no";
  const esBiomedico = equipo?.categoria?.grupo === "Biomédico";
  const caracteristicasEspeciales = Object.entries(
    equipo?.caracteristicas || {},
  ).filter(
    ([, value]) =>
      value !== null && value !== undefined && `${value}`.trim() !== "",
  );

  const handleGenerarEtiqueta = async (id) => {
    try {
      setLoading("etiqueta");
      const token = localStorage.getItem("token");
      const API = process.env.REACT_APP_API_URL;
      const response = await fetch(`${API}/equipos/${id}/etiqueta`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `etiqueta_${equipo.codigo}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Error al generar etiqueta:", err);
    } finally {
      setLoading(null);
    }
  };

  const handleGenerarHv = async (id) => {
    try {
      setLoading("hv");
      const token = localStorage.getItem("token");
      const API = process.env.REACT_APP_API_URL;
      const response = await fetch(`${API}/reportes/equipoHV/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hv_${equipo.codigo}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Error al generar hoja de vida:", err);
    } finally {
      setLoading(null);
    }
  };

  const handleGenerarHvMinisterio = async (id) => {
    try {
      setLoading("hvMin");
      const token = localStorage.getItem("token");
      const API = process.env.REACT_APP_API_URL;
      const response = await fetch(
        `${API}/reportes/equipoHV-ministerio/${id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(
          data?.message || "No se pudo generar el formato MinSalud",
        );
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hv_ministerio_${equipo.codigo}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Error al generar HV MinSalud:", err);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="space-y-5">

      {/* ── Action toolbar ── */}
      <div className="flex flex-wrap items-center gap-2">
        {garantiaActiva && (
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-sky-600 bg-sky-50 border border-sky-200 rounded-full px-3 py-1">
            <Award className="h-3.5 w-3.5" /> En garantía
          </span>
        )}

        {!publicView && (
          <div className="flex flex-wrap items-center gap-2 ml-auto">
            {loading && (
              <span className="inline-flex items-center gap-1.5 text-xs text-blue-600 bg-blue-50 rounded-lg px-3 py-1.5">
                <LoaderPinwheel className="h-3.5 w-3.5 animate-spin" />
                Generando…
              </span>
            )}
            <button
              disabled={loading !== null}
              onClick={() => handleGenerarEtiqueta(equipo._id)}
              className="inline-flex items-center gap-1.5 text-sm text-gray-700 border border-gray-200 bg-white hover:bg-gray-50 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-50 disabled:pointer-events-none"
            >
              <Tag className="h-3.5 w-3.5" /> Imprimir etiqueta
            </button>
            <button
              disabled={loading !== null}
              onClick={() => handleGenerarHv(equipo._id)}
              className="inline-flex items-center gap-1.5 text-sm text-gray-700 border border-gray-200 bg-white hover:bg-gray-50 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-50 disabled:pointer-events-none"
            >
              <Printer className="h-3.5 w-3.5" /> Hoja de vida
            </button>
            <button
              disabled={loading !== null || !esBiomedico}
              onClick={() => handleGenerarHvMinisterio(equipo._id)}
              className="inline-flex items-center gap-1.5 text-sm text-gray-700 border border-gray-200 bg-white hover:bg-gray-50 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-50 disabled:pointer-events-none"
            >
              <FileDown className="h-3.5 w-3.5" /> Formato MinSalud
            </button>
          </div>
        )}
      </div>

      {/* ── Datos del equipo ── */}
      <SectionCard icon={Zap} title="Datos del equipo">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
          <InfoField label="Marca" value={equipo.marca?.name} />
          <InfoField label="Modelo" value={equipo.modelo?.name} />
          <InfoField label="Registro Invima" value={mantenimiento?.invima} />
          <InfoField label="Propiedad" value={equipo.propiedad} />
          <InfoField label="Condición" value={equipo.condicion} />
          <InfoField
            label="Costo"
            value={
              equipo.costo
                ? `$ ${Number(equipo.costo).toLocaleString()}`
                : null
            }
          />
          <InfoField
            label="Proveedor"
            value={
              equipo.proveedor
                ? `[${equipo.proveedor.nit}] ${equipo.proveedor.name}`
                : null
            }
          />
        </div>

        {caracteristicasEspeciales.length > 0 && (
          <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-3 mt-1">
            <p className="text-xs font-semibold text-blue-900 mb-2 uppercase tracking-wide">
              Características especiales
            </p>
            <div className="flex flex-wrap gap-2">
              {caracteristicasEspeciales.map(([key, value]) => (
                <span
                  key={key}
                  className="inline-flex items-center rounded-full border border-blue-200 bg-white px-3 py-1 text-xs text-blue-900"
                >
                  <strong className="mr-1">{key}:</strong> {value}
                </span>
              ))}
            </div>
          </div>
        )}
      </SectionCard>

      {/* ── Mantenimiento ── */}
      <SectionCard icon={Wrench} iconColor="text-orange-500" title="Mantenimiento">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          <InfoField
            label="Periodicidad mantenimiento"
            value={mantenimiento?.periocidadMantenimiento}
          />
          <InfoField
            label="Periodicidad calibración"
            value={mantenimiento?.periocidadCalibracion}
          />
          <InfoField
            label="Requiere calibración"
            value={
              mantenimiento?.requiereCalibracion === "si" ? "Sí" : "No"
            }
          />
          <InfoField
            label="Descripción"
            value={mantenimiento?.descripcionEquipo}
          />
          <InfoField
            label="Registro técnico"
            value={mantenimiento?.registroTecnico}
          />
        </div>
      </SectionCard>

      {/* ── Clasificación ── */}
      <SectionCard
        icon={ClipboardCheck}
        iconColor="text-violet-500"
        title="Clasificación del equipo"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4">
          <InfoField label="Uso" value={mantenimiento?.usos} />
          <InfoField label="Clase tecnológica" value={mantenimiento?.claseTecno} />
          <InfoField label="Clase de riesgo" value={mantenimiento?.claseRiesgo} />
          <InfoField
            label="Clasificación biomédica"
            value={mantenimiento?.clasificacion}
          />
        </div>
      </SectionCard>

      {/* ── Estado técnico ── */}
      <div className="flex items-center justify-between text-xs text-gray-400 pt-1 border-t border-gray-100">
        <span className="flex items-center gap-1.5">
          <ClipboardCheck className="h-3.5 w-3.5 text-green-500" />
          Estado técnico
        </span>
        <span>
          Última actualización:{" "}
          <span className="font-medium text-gray-600">
            {new Date(
              equipo.updatedAt || equipo.createdAt,
            ).toLocaleDateString("es-CO")}
          </span>
        </span>
      </div>
    </div>
  );
};

export default FichaTecnica;
